import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LockAnalysis } from '@/accessControl/LockAnalysis.molecule';
import { VersionHistoryOperations, useVersionState } from '@/core/hooks/useVersionState.hook';
import { Modal } from '@seeqdev/qomponents';
import type { VersionState } from '@/core/hooks/useVersionState.hook';
import type { WorkbookApprovalFlow, WorkbookApprovalFlowStep } from '@/worksheet/worksheet.types';

type ApprovalStepModalProps = {
  step: WorkbookApprovalFlowStep;
  futureStep: WorkbookApprovalFlowStep | undefined;
  approvalFlow: WorkbookApprovalFlow | null;
  itemType: string;
  isSubmitting: boolean;
  hasLockingFailedPreviouslyDueToGlobalItems?: boolean;
  onSubmit: (versionState?: VersionState) => Promise<void>;
  onClose: () => void;
  isItemLocked?: boolean;
};

export const ApprovalStepModal = ({
  step,
  futureStep,
  approvalFlow,
  itemType,
  isSubmitting,
  hasLockingFailedPreviouslyDueToGlobalItems = false,
  onSubmit,
  onClose,
  isItemLocked = false,
}: ApprovalStepModalProps) => {
  const { t } = useTranslation();
  const versionState = useVersionState();
  const [understandLockingIsPermanent, setUnderstandLockingIsPermanent] = useState(false);
  const needsLocking = step.lockRequired && !isItemLocked;
  const versionSelectionValid =
    versionState.versionHistoryOperation !== VersionHistoryOperations.Update ||
    versionState.selectedVersionHistory !== null;
  const canSubmit =
    !isSubmitting &&
    (!needsLocking || (understandLockingIsPermanent && !versionState.newWorkbookNameError && versionSelectionValid));

  return (
    <Modal
      open={true}
      onClose={onClose}
      onSubmit={() => onSubmit(step.lockRequired ? versionState : undefined)}
      disableSubmitButton={!canSubmit}
      submitButtonLabel={t('APPROVAL_FLOWS.APPROVE')}
      cancelButtonLabel={t('CANCEL')}
      testId="approveStepModal"
      title={t('APPROVAL_FLOWS.APPROVE_STEP_TITLE', { stepName: step.name, position: step.position })}>
      <div className="tw:flex tw:flex-col tw:gap-4">
        {needsLocking ? (
          <>
            <p>
              {t('APPROVAL_FLOWS.APPROVE_STEP_LOCK_WARNING', {
                stepName: step.name,
                flowName: approvalFlow?.name,
                itemType,
              })}
            </p>
            <p>{t('APPROVAL_FLOWS.APPROVE_STEP_LOCK_CHECK_WORK', { itemType })}</p>
            <LockAnalysis
              versionState={versionState}
              disabled={isSubmitting}
              checkboxId="understand-approve-step-lock-checkbox"
              checkboxLabel={t('APPROVAL_FLOWS.APPROVE_STEP_UNDERSTAND_LOCK', { itemType })}
              isChecked={understandLockingIsPermanent}
              onCheckChange={setUnderstandLockingIsPermanent}
              hasLockingFailedPreviouslyDueToGlobalItems={hasLockingFailedPreviouslyDueToGlobalItems}
            />
          </>
        ) : (
          <p>
            {futureStep
              ? t('APPROVAL_FLOWS.APPROVE_STEP_DESCRIPTION', {
                  stepName: futureStep.name,
                  position: futureStep.position,
                  itemType,
                })
              : t('APPROVAL_FLOWS.APPROVE_PRODUCTION_VERSION_DESCRIPTION', { stepName: step?.name, itemType })}
          </p>
        )}
      </div>
    </Modal>
  );
};
